import { useEffect, useState } from "react";
import { ShoppingCart, Plus, Minus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // {id: qty}
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const add = (id) => {
    setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
    toast.success("Added to cart");
  };
  const dec = (id) => setCart((c) => {
    const next = { ...c, [id]: (c[id] || 0) - 1 };
    if (next[id] <= 0) delete next[id];
    return next;
  });
  const remove = (id) => setCart((c) => { const n = { ...c }; delete n[id]; return n; });

  const cartItems = Object.entries(cart).map(([id, qty]) => {
    const p = products.find((x) => x.id === id);
    return p ? { ...p, qty } : null;
  }).filter(Boolean);

  const subtotal = cartItems.reduce((sum, it) => sum + it.price * it.qty, 0);
  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);

  const checkout = async () => {
    if (cartItems.length === 0) return;
    const product_ids = [];
    cartItems.forEach((it) => { for (let i = 0; i < it.qty; i++) product_ids.push(it.id); });
    try {
      setCheckoutLoading(true);
      const origin_url = window.location.origin;
      const res = await api.post("/checkout/session", { product_ids, origin_url });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  const filtered = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <div className="container-wrap pt-16 md:pt-20 pb-24">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="overline mb-5"><span className="gold-divider align-middle mr-3" />Shop</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-brand-dark max-w-3xl leading-[1.0]">
            Wear the mission. Read the stories.
          </h1>
          <p className="mt-6 max-w-2xl text-brand-dark/70 text-lg leading-relaxed">
            Every purchase funds free books and author-led programs for kids in New Orleans. Proudly carrying books written by our Young Authors.
          </p>
        </div>
        <button onClick={() => setCartOpen(true)} data-testid="shop-cart-button" className="relative bg-brand-dark text-white rounded-full px-5 py-3 flex items-center gap-2 hover:bg-brand-purple transition">
          <ShoppingCart className="w-4 h-4" /> Cart
          {cartCount > 0 && <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-brand-gold text-brand-dark text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{cartCount}</span>}
        </button>
      </div>

      <div className="mt-10 flex gap-2">
        {["all", "book", "merch"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}-button`}
            className={`px-5 py-2 rounded-full text-sm font-medium border transition ${
              filter === f ? "bg-brand-purple border-brand-purple text-white" : "bg-white border-brand-purple/20 text-brand-dark hover:bg-brand-lavender/50"
            }`}
          >
            {f === "all" ? "All" : f === "book" ? "Books" : "Merch"}
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="products-grid">
        {loading && <p className="text-brand-dark/60">Loading…</p>}
        {filtered.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="bg-white rounded-3xl border border-brand-purple/15 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col">
            <div className="h-64 overflow-hidden bg-brand-lavender/40">
              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition" />
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <p className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-bold mb-2">{p.category}</p>
              <h3 className="font-serif-display text-xl text-brand-dark mb-2">{p.name}</h3>
              <p className="text-brand-dark/70 text-sm leading-relaxed flex-1 mb-5">{p.description}</p>
              <div className="flex items-center justify-between">
                <p className="font-serif-display text-2xl text-brand-purple">${p.price.toFixed(2)}</p>
                <button onClick={() => add(p.id)} data-testid={`add-to-cart-${p.id}`} className="btn-primary py-2 px-5 text-sm">
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-brand-dark/60 backdrop-blur" onClick={() => setCartOpen(false)}>
          <div className="bg-white w-full max-w-md h-full p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cart-drawer">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-serif-display text-2xl text-brand-dark">Your Cart</h3>
              <button onClick={() => setCartOpen(false)} className="text-brand-dark/60 hover:text-brand-dark" data-testid="cart-close-button"><X /></button>
            </div>
            {cartItems.length === 0 && <p className="text-brand-dark/60">Your cart is empty.</p>}
            <div className="space-y-4">
              {cartItems.map((it) => (
                <div key={it.id} className="flex gap-3 border-b border-brand-purple/10 pb-4" data-testid={`cart-item-${it.id}`}>
                  <img src={it.image_url} alt={it.name} className="w-20 h-20 object-cover rounded-xl" />
                  <div className="flex-1">
                    <p className="font-medium text-brand-dark text-sm">{it.name}</p>
                    <p className="text-brand-purple font-semibold mt-1">${(it.price * it.qty).toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => dec(it.id)} data-testid={`cart-dec-${it.id}`} className="w-7 h-7 rounded-full border border-brand-purple/30 flex items-center justify-center hover:bg-brand-lavender"><Minus className="w-3 h-3" /></button>
                      <span className="text-sm w-6 text-center">{it.qty}</span>
                      <button onClick={() => add(it.id)} data-testid={`cart-inc-${it.id}`} className="w-7 h-7 rounded-full border border-brand-purple/30 flex items-center justify-center hover:bg-brand-lavender"><Plus className="w-3 h-3" /></button>
                      <button onClick={() => remove(it.id)} data-testid={`cart-remove-${it.id}`} className="ml-auto text-brand-dark/50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {cartItems.length > 0 && (
              <div className="mt-6 border-t border-brand-purple/10 pt-5">
                <div className="flex justify-between mb-4">
                  <span className="text-brand-dark/70">Subtotal</span>
                  <span className="font-serif-display text-2xl text-brand-dark" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <button onClick={checkout} disabled={checkoutLoading} data-testid="checkout-button" className="btn-primary w-full disabled:opacity-60">
                  {checkoutLoading ? "Redirecting…" : "Checkout with Stripe"}
                </button>
                <p className="text-xs text-brand-dark/50 mt-3 text-center">Secure payment processed by Stripe.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
